import React from 'react';

export interface BulkSendResultData {
  sent: number;
  failed: number;
  already_sent: number;
  results: Array<{
    order?: string | number;
    consignment_id?: string;
    tracking_code?: string;
    success?: boolean;
    already_sent?: boolean;
    message?: string;
  }>;
}

interface BulkSendResultModalProps {
  result: BulkSendResultData | null;
  onClose: () => void;
}

export const BulkSendResultModal: React.FC<BulkSendResultModalProps> = ({
  result,
  onClose,
}) => {
  if (!result) return null;

  const headerBg = result.failed === 0
    ? 'linear-gradient(135deg, #059669, #065f46)'
    : result.sent === 0 && result.already_sent === 0
    ? 'linear-gradient(135deg, #dc2626, #991b1b)'
    : 'linear-gradient(135deg, #d97706, #92400e)';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)',
      padding: '16px',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '14px',
        width: '100%',
        maxWidth: '680px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          background: headerBg,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '22px' }}>📦</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Send to Steadfast — Results</h2>
              <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                ✅ {result.sent} sent
                {result.already_sent > 0 && <> &nbsp;·&nbsp; 🔵 {result.already_sent} already sent</>}
                {result.failed > 0 && <> &nbsp;·&nbsp; ❌ {result.failed} failed</>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '20px',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Body Table */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order #</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Tracking Code</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_sent ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {r.already_sent ? (
                      <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔵 Already Sent</span>
                    ) : r.success ? (
                      <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Sent</span>
                    ) : (
                      <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.consignment_id ? '#1d4ed8' : '#9ca3af', fontWeight: r.consignment_id ? '700' : '400' }}>
                    {r.consignment_id || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.tracking_code ? '#1d4ed8' : '#9ca3af' }}>
                    {r.tracking_code || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', color: r.success ? '#6b7280' : '#dc2626', fontSize: '12px' }}>
                    {r.message || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 22px',
              background: '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkSendResultModal;
